import { getSupabaseBrowserClient } from '../lib/supabase'

export type OrganisationalAiSolutionDetail = {
  id: string
  organisationId: string
  name: string
  businessOwnerMembershipId: string | null
  businessOwnerName: string | null
  technicalOwnerMembershipId: string | null
  technicalOwnerName: string | null
  operatingEnvironment: string | null
  intendedAudience: string[]
  containsMultipleAgents: boolean | null
  technicalDocumentationUrl: string | null
  status: string
  canEdit: boolean
  productContext: {
    adoptionId: string
    productId: string | null
    productName: string | null
    privateProductId: string | null
    supplierName: string | null
    offeringId: string | null
    offeringName: string | null
    adoptionStatus: string
  } | null
  useCases: { id: string; title: string; businessPurpose: string; operationalState: string }[]
  updatedAt: string
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

async function invoke(functionName: string, args: Record<string, unknown>) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}
function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function maybeText(value: unknown): string | null { return typeof value === 'string' && value ? value : null }
function textArray(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [] }

export async function getOrganisationalAiSolution(solutionId: string): Promise<OrganisationalAiSolutionDetail> {
  const row = record(await invoke('get_organisation_ai_solution', { p_solution_id: solutionId }))
  const product = row.productContext ? record(row.productContext) : null
  return {
    id: text(row.id),
    organisationId: text(row.organisationId),
    name: text(row.name),
    businessOwnerMembershipId: maybeText(row.businessOwnerMembershipId),
    businessOwnerName: maybeText(row.businessOwnerName),
    technicalOwnerMembershipId: maybeText(row.technicalOwnerMembershipId),
    technicalOwnerName: maybeText(row.technicalOwnerName),
    operatingEnvironment: maybeText(row.operatingEnvironment),
    intendedAudience: textArray(row.intendedAudience),
    containsMultipleAgents: typeof row.containsMultipleAgents === 'boolean' ? row.containsMultipleAgents : null,
    technicalDocumentationUrl: maybeText(row.technicalDocumentationUrl),
    status: text(row.status),
    canEdit: row.canEdit === true,
    productContext: product ? {
      adoptionId: text(product.adoptionId),
      productId: maybeText(product.productId),
      productName: maybeText(product.productName),
      privateProductId: maybeText(product.privateProductId),
      supplierName: maybeText(product.supplierName),
      offeringId: maybeText(product.offeringId),
      offeringName: maybeText(product.offeringName),
      adoptionStatus: text(product.adoptionStatus),
    } : null,
    useCases: Array.isArray(row.useCases) ? row.useCases.map((item) => {
      const useCase = record(item)
      return { id: text(useCase.id), title: text(useCase.title), businessPurpose: text(useCase.businessPurpose), operationalState: text(useCase.operationalState) }
    }) : [],
    updatedAt: text(row.updatedAt),
  }
}

export async function updateOrganisationalAiSolution(input: {
  solutionId: string
  name: string
  businessOwnerMembershipId?: string | null
  technicalOwnerMembershipId?: string | null
  operatingEnvironment?: string | null
  intendedAudience?: string[]
  containsMultipleAgents?: boolean | null
  technicalDocumentationUrl?: string | null
}): Promise<void> {
  await invoke('update_organisation_ai_solution', {
    p_solution_id: input.solutionId,
    p_name: input.name,
    p_business_owner_membership_id: input.businessOwnerMembershipId || null,
    p_technical_owner_membership_id: input.technicalOwnerMembershipId || null,
    p_operating_environment: input.operatingEnvironment || null,
    p_intended_audience: input.intendedAudience ?? [],
    p_contains_multiple_agents: input.containsMultipleAgents ?? null,
    p_technical_documentation_url: input.technicalDocumentationUrl || null,
  })
}
